interface Props {
  onCta: () => void;
}

export function Hero({ onCta }: Props): JSX.Element {
  return (
    <section className="relative overflow-hidden pt-24 pb-20 md:pt-32 md:pb-28">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[600px] bg-lime-500/10 blur-3xl rounded-full" />
        <div className="absolute top-40 right-10 w-72 h-72 bg-lime-500/5 blur-3xl rounded-full" />
      </div>
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-ink-800 border border-ink-700 rounded-full px-3 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-lime-500 animate-pulse" />
            <span className="text-xs text-ink-300 uppercase tracking-wider">Nowa generacja nauki języków</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-white">
            Twój <span className="text-lime-500">nowoczesny</span> nauczyciel angielskiego <span className="bg-gradient-to-r from-lime-400 to-lime-600 bg-clip-text text-transparent">24/7</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ink-300 leading-relaxed max-w-xl">
            Certyfikowany lektor na żywo i nielimitowana praktyka z inteligentnym awatarem.
            Każda sesja oceniana automatycznie — widzisz dokładnie, ile postępu zrobiłeś.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onCta}
              className="bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold rounded-xl px-6 py-3.5 text-base shadow-glow-lime transition"
            >
              Zacznij za darmo →
            </button>
            <a
              href="#how"
              className="border border-ink-600 hover:border-ink-500 text-white rounded-xl px-6 py-3.5 text-base font-medium transition text-center"
            >
              Zobacz jak to działa
            </a>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-ink-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-lime-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              Bez karty kredytowej
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-lime-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
              Pierwsza lekcja za 0 zł
            </div>
          </div>
        </div>

        <div className="relative animate-fade-up [animation-delay:200ms]">
          <div className="relative aspect-square max-w-md mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-lime-500/20 to-transparent rounded-3xl blur-2xl" />
            <div className="absolute inset-4 bg-ink-800 border border-ink-700 rounded-3xl overflow-hidden shadow-2xl animate-float">
              <div className="h-full flex flex-col">
                <div className="px-4 py-3 border-b border-ink-700 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="w-2 h-2 rounded-full bg-lime-500" />
                  <span className="text-xs text-ink-400 ml-2">Lekcja z Michaelem · live</span>
                </div>
                <div className="flex-1 relative bg-gradient-to-br from-ink-900 to-ink-800 flex items-center justify-center">
                  <div className="relative w-44 h-44 rounded-full p-1 bg-gradient-to-br from-lime-400/60 to-lime-600/20 shadow-glow-lime">
                    <img
                      src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=faces&auto=format&q=80"
                      alt="Michael — Twój nauczyciel"
                      loading="lazy"
                      className="w-full h-full rounded-full object-cover ring-2 ring-ink-900"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-lime-500 text-ink-900 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1 shadow-lg">
                      <span className="w-1.5 h-1.5 rounded-full bg-ink-900 animate-pulse"></span> Live
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-ink-900/80 backdrop-blur border border-ink-700 rounded-xl px-3 py-2 text-xs text-white">
                      <span className="text-lime-500 font-semibold">Michael:</span> Tell me about your weekend plans.
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-ink-700 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-lime-500 font-bold text-lg">92</div>
                    <div className="text-[10px] text-ink-400 uppercase tracking-wider">Płynność</div>
                  </div>
                  <div>
                    <div className="text-lime-500 font-bold text-lg">88</div>
                    <div className="text-[10px] text-ink-400 uppercase tracking-wider">Wymowa</div>
                  </div>
                  <div>
                    <div className="text-lime-500 font-bold text-lg">B1+</div>
                    <div className="text-[10px] text-ink-400 uppercase tracking-wider">Poziom</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 bg-lime-500 text-ink-900 rounded-2xl px-3 py-2 text-xs font-semibold shadow-lg animate-pulse-slow">
              🎯 +47 słów dziś
            </div>
            <div className="absolute -bottom-4 -left-4 bg-ink-800 border border-ink-700 rounded-2xl px-3 py-2 text-xs font-medium text-white shadow-lg">
              🔥 14-dniowy streak
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
